import React, { useState } from 'react';
import {
	Box,
	Collapse,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Radio,
	TextField,
} from '@mui/material';
import { useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { BillingEmail } from 'common/modules/billing';
import { isValidEmail } from 'common/utils/validation';
import { useTranslation } from 'services/localization';

interface Props {
	value: BillingEmail;
	onChange: (value: BillingEmail) => void;
}
const BillingEmailForm = (props: Props) => {
	const { t } = useTranslation();
	const { value, onChange } = props;
	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo);

	const [isDirty, setDirty] = useState<boolean>(false);

	const handleTypeChange = (type: BillingEmail['type']) => () => {
		switch (type) {
			case 'default': {
				return onChange({
					type: 'default',
				});
			}
			case 'store': {
				return onChange({
					type: 'store',
				});
			}
			case 'custom': {
				return onChange({
					type: 'custom',
					email: '',
				});
			}
		}
	};

	const handleBillingEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange({
			type: 'custom',
			email: e.target.value,
		});
	};

	const isInvalidEmail = value.type === 'custom' && !isValidEmail(value.email);

	return (
		<>
			<List>
				<ListItem button onClick={handleTypeChange('default')}>
					<ListItemIcon>
						<Radio color="primary" checked={value.type === 'default'} />
					</ListItemIcon>
					<ListItemText
						primary={t('BillingInformation.accountEmailAddress', 'Account email address')}
						secondary={t('BillingInformation.accountEmailAddressHelper', {
							email: shopPublicInfo?.email ?? '',
							defaultValue: 'Use the account email address ({{email}})',
						})}
					/>
				</ListItem>
				<ListItem button onClick={handleTypeChange('store')}>
					<ListItemIcon>
						<Radio color="primary" checked={value.type === 'store'} />
					</ListItemIcon>
					<ListItemText
						primary={t('BillingInformation.storeEmailAddress', 'Store email address')}
						secondary={t(
							'BillingInformation.storeEmailAddressHelper',
							'Use the email address of the store which the invoice is for',
						)}
					/>
				</ListItem>
				<ListItem button onClick={handleTypeChange('custom')}>
					<ListItemIcon>
						<Radio color="primary" checked={value.type === 'custom'} />
					</ListItemIcon>
					<ListItemText
						primary={t('BillingInformation.customEmailAddress', 'Custom email address')}
					/>
				</ListItem>
			</List>
			<Collapse in={value.type === 'custom'} onExited={() => setDirty(false)}>
				<Box ml={9}>
					<TextField
						fullWidth
						variant="outlined"
						label={t('BillingInformation.billingEmailTitle', 'Billing email')}
						value={value.type === 'custom' ? value.email : ''}
						onChange={handleBillingEmailChange}
						onBlur={() => setDirty(true)}
						error={isDirty && isInvalidEmail}
						helperText={isDirty && isInvalidEmail ? t('common:validation.isNotValidEmail') : ''}
					/>
				</Box>
			</Collapse>
		</>
	);
};

export default BillingEmailForm;
